import api from '@/services/api';
import { Charge } from '@/types/charge';
import { useQuery } from '@tanstack/react-query';

// Query keys
export const chargeKeys = {
  all: ['charges'] as const,
  details: () => [...chargeKeys.all, 'detail'] as const,
  detail: (id: string) => [...chargeKeys.details(), id] as const,
  byEnrollment: (enrollmentId: string) => [...chargeKeys.all, 'enrollment', enrollmentId] as const,
};

// Single charge
export function useCharge(id: string | undefined) {
  return useQuery({
    queryKey: chargeKeys.detail(id || ''),
    queryFn: async () => {
      if (!id) throw new Error('Charge ID is required');
      const response = await api.get<Charge>(`/charge/${id}`);
      return response.data;
    },
    enabled: !!id,
  });
}

// Charges by enrollment (paginated)
export function useChargesByEnrollment({
  enrollmentId,
  page = 1,
  limit = 10,
}: {
  enrollmentId: string | undefined;
  page?: number;
  limit?: number;
}) {
  return useQuery({
    queryKey: [...chargeKeys.byEnrollment(enrollmentId || ''), { page, limit }],
    queryFn: async () => {
      if (!enrollmentId) throw new Error('Enrollment ID is required');
      const response = await api.get<{
        data: Charge[];
        total: number;
        page: number;
        limit: number;
      }>(`/enrollments/${enrollmentId}/charges`, {
        params: { page, limit },
      });
      return response.data;
    },
    enabled: !!enrollmentId,
  });
}
